import { h } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { getIcon } from 'utils/icons';
import { toast } from 'utils/modal';
import { joinClasses } from 'utils/utils';
import { findInVNode } from 'utils/vnode';

import FieldBase from 'components/editor/settings/fieldBase';
import InputWithKeywords from 'components/inputWithKeywords/inputWithKeywords';

import s from 'components/editor/settings/simpleField.sss';

export default class SimpleField extends FieldBase {
	s = s;

	constructor(props) {
		super(props);

		this.onInput = this.onInput.bind(this);
		this.onReset = this.onReset.bind(this);
		this.togglePassword = this.togglePassword.bind(this);
		this.copyToClipboard = this.copyToClipboard.bind(this);
	}

	componentDidMount() {
		super.componentDidMount?.();
		if (!this.value) {
			const input = this.base.querySelector(`#${this.state.id}`);
			if (input) {
				input.value = '';
			}
		}
	}

	getKeywords() {
		const datalist = findInVNode(this.props.children, 'datalist[type="keywords"]');
		if (!datalist) return [];
		return datalist.children.filter(option => !!option).map(option => ({ value: option?.attributes?.value, label: option.children, example: option?.attributes?.example, disabled: !!option?.attributes?.disabled }));
	}

	onInput(e) {
		this.props.onInput?.(e);

		const value = e.target.value;
		if (value.length >= this.props.maxLength) {
			// Note: this can break controlled inputs if this setState happens before the update of the value prop from the parent
			//   as it renders which temporarily reverts the input value then corrects it once the parent updates the prop - this is invisible to the user but loses the cursor position
			this.setState({ error: `You have reached the maximum length of ${this.props.maxLength} characters` });
		} else if (this.state.error) {
			this.setState({ error: null });
		}
		if (!this.props.path) return;

		// If the value is empty and the field can inherit, remove the value from the data store to allow inheritance
		if (this.canInherit && !value) {
			jsonPointer.removeDeep(Store.get().data, this.props.path);
		} else {
			jsonPointer.setDeep(Store.get().data, this.props.path, value);
		}

		this.updateAlsoSet(value);
	}

	onReset() {
		this.updateAlsoSet();
	}

	updateAlsoSet(value) {
		if (!this.props.alsoSet) return;

		this.props.alsoSet.forEach(({ path, method, condition }) => {
			if (condition()) {
				jsonPointer.setDeep(Store.get().data, path, method(value));
			}
		});
	}

	togglePassword(e) {
		this.setState({ showPassword: e.currentTarget.checked });
	}

	async copyToClipboard() {
		const value = this.value || this.inheritedValue;
		if (!value) return;
		try {
			await navigator.clipboard.writeText(value);
			this.setState({ copied: true });
			toast('Copied to clipboard', { duration: 2000, icon: 'clipboard' });
			setTimeout(() => this.setState({ copied: false }), 1000);
		} catch (err) {
			console.error('Failed to copy text: ', err);
		}
	}

	renderInheritIndicator() {
		const base = super.renderInheritIndicator();
		if (base) base.attributes.onReset = this.onReset;
		return base;
	}

	renderInput() {
		const props = this.props;
		const value = this.value || (this.readonly && this.inheritedValue) || undefined;
		const sharedProps = {
			id: this.state.id,
			name: props.name,
			placeholder: this.placeholder,
			maxLength: props.maxLength,
			required: props.required,
			readonly: this.readonly,
			autocomplete: props.autocomplete,
			disabled: this.disabled,
			onInput: this.onInput,
		};
		// Do not use the value attribute if uncontrolled to avoid resetting on render
		// Value is controlled if a path is provided or if a value prop is provided
		if (value != undefined || props.path) sharedProps.value = value ?? '';

		if (props.multiline) {
			return <textarea {...sharedProps} rows={props.rows || 6} key="textarea" />;
		}

		let type = props.type || 'text';
		const keywords = this.getKeywords();
		if (type === 'text' && keywords?.length) {
			return <InputWithKeywords type="text" {...sharedProps} class={s.inputWithKeywords} keywords={keywords} key="rich-input" />;
		}

		if (type === 'password' && this.state.showPassword) {
			type = 'text';
		}

		return <input type={type} {...sharedProps} key="input" />;
	}

	renderCopyButton() {
		const { copied } = this.state;
		return (
			<button type="button" class={joinClasses(s.copyButton, copied && s.success)} onClick={this.copyToClipboard} title="Copy to clipboard" key="copy">
				{getIcon(copied ? 'check' : 'copy')}
			</button>
		);
	}

	renderField() {
		const props = this.props;
		const state = this.state;

		return (
			<div class={joinClasses(this.s.field, this.props.gray && this.s.gray)} key="field">
				{getIcon(props.icon)}
				{!!props.preText && <div class={s.preText} key="pre">{props.preText}</div>}
				{this.renderInput()}
				{props.copyable && this.renderCopyButton()}
				{props.type === 'password' && <input type="checkbox" class={s.togglePassword} checked={state.showPassword} onInput={this.togglePassword} key="show-password" />}
			</div>
		);
	}
}
